import * as React from 'react';
import Breadcrumb, { BreadcrumbSection, BreadcrumbStatus } from '../../common/breadcrumb';
import { Stepper } from 'frontend-toolkit';
import { CustomHeader } from '../program-guide/program-guide';
import * as CreateSeason from '../../../core/gql/mutations/create-season';
import { useMutation } from '@apollo/client';
import DivisionRules from './division-rules';
import NameAndPeriod from './name-and-period';
import AgeRange from './age-range';
import GendersByAge from './genders-by-age';
import { DivisionsReview, Division } from './divisions-review';
import SelectAgeGroups from './select-age-groups';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import { DateService, DateFormat } from 'frontend-toolkit';
import { GqlProgramExpanded } from '../../../core/gql/fragments/program';
import { GqlOrganization } from '../../../core/gql/fragments/organization';
import { ActivitySeasonType } from '../../../models/season';
import ConfirmationModal, { ConfirmationType } from '../program-guide/confirmation-modal';
import { TEAMSNAP_URL } from 'core/constants';

type Props = {
  program: GqlProgramExpanded;
  organization: GqlOrganization;
  setOpen: (x: boolean) => void;
  onCreationComplete?: () => void;
};

export type SeasonData = {
  name: string;
  organizeMultipleDivisions: boolean;
  organizeDivisionsByAge: boolean | null;
  ageGroupTitle: AgeGroupFormats | null;
  organizeDivisionsByGender: boolean | null;
  genders: string[];
  setDatesLater: boolean;
  start: Date | null;
  end: Date | null;
  ageGroups: { id: string; genders: string[] }[];
  ageGroupsOptions: any[];
  ageRanges: string[] | null;
  ageRangeOther: string[] | null;
  divisions: Division[];
  tsDivisionId?: number;
};

export enum SeasonGuideSteps {
  NAME = 'Create Season',
  DIVISION = 'Division rules',
  AGE_RANGE = 'Age Range & Title',
  AGE_GROUPS = 'Select Age Groups',
  GENDERS_BY_AGE = 'Select Gender Groups',
  DIVISIONS_REVIEW = 'Review Divisions',
  DIVISIONS_CONFIRMATION = 'Confirm Divisions',
  CREATING_SEASON_DIVISION = 'Creating Seasons/Divisions',
}

export enum AgeGroupFormats {
  AGE_AND_UNDER = '14 & Under',
  UNDER = 'Under 14',
  BIRTHYEAR = 'Birth year',
  SCHOOL_GRADE = 'School Grade',
  GRADUATION_YEAR = 'Graduation Year',
  OTHER = 'Other',
  AGE_RANGE = 'Age range',
  GENDER_GROUPS = 'Gender groups',
}

const programBreadcrumb = {
  name: 'Basic Program Info',
  status: BreadcrumbStatus.COMPLETED,
  children: [
    {
      name: 'Program Type',
      status: BreadcrumbStatus.COMPLETED,
    },
    {
      name: 'Sport',
      status: BreadcrumbStatus.COMPLETED,
    },
    {
      name: 'Name and Description',
      status: BreadcrumbStatus.COMPLETED,
    },
  ],
};

const seasonDefaults: SeasonData = {
  name: '',
  organizeMultipleDivisions: true,
  organizeDivisionsByAge: null,
  organizeDivisionsByGender: null,
  ageGroupTitle: null,
  setDatesLater: false,
  genders: [],
  start: new Date(),
  end: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
  ageGroups: [],
  ageGroupsOptions: [],
  ageRanges: null,
  ageRangeOther: null,
  divisions: [],
};

export const SeasonGuideContext = React.createContext<{
  season: SeasonData;
  setSeason: (x: Partial<SeasonData>) => void;
}>({ season: seasonDefaults, setSeason: () => {} });

const shouldDisableDivisionRulesStep = (season: SeasonData) => {
  if (!season.organizeMultipleDivisions) return false;

  if (season.organizeDivisionsByGender === null || season.organizeDivisionsByAge === null) return true;

  if (season.organizeDivisionsByGender && !season.genders.length) return true;
};

const shouldDisableAgeRangeNext = (season: SeasonData) => {
  if (
    season.ageGroupTitle &&
    season.ageGroupTitle != AgeGroupFormats.OTHER &&
    season?.ageRanges &&
    +season?.ageRanges[0] > +season?.ageRanges[1]
  ) {
    return true;
  } else if (
    season.ageGroupTitle &&
    season.ageGroupTitle == AgeGroupFormats.OTHER &&
    (!season?.ageRangeOther || (season?.ageRangeOther && season?.ageRangeOther.length <= 0))
  ) {
    return true;
  }

  return season.ageGroupTitle ? false : true;
};

const SeasonGuide = ({ setOpen, organization, program, onCreationComplete }: Props) => {
  const [season, setSeason] = React.useState<SeasonData>(seasonDefaults);
  const [currentBreadcrumb, updateCurrentBreadcrumb] = React.useState<SeasonGuideSteps>(SeasonGuideSteps.NAME);
  const [currentStep, setCurrentStep] = React.useState(0);
  const [createSeason] = useMutation(CreateSeason.MUTATION);
  const [confirmationType, setConfirmationType] = React.useState(ConfirmationType.SEASON_CREATED);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);

  const submitCreateSeason = async () => {
    try {
      const resp = await createSeason({
        variables: {
          name: season.name,
          startDate: DateService.dateToString(season.start, DateFormat.YYYY_MM_DD),
          endDate: DateService.dateToString(season.end, DateFormat.YYYY_MM_DD),
          country: organization.country,
          zipCode: organization.zipCode,
          timezone: organization.timezone,
          initialDivisions: season.divisions,
          sportId: program.sportId,
          programId: program.id,
          areaOfOperation: program.areaOfOperation?.id,
          type: ActivitySeasonType.ClubSeason,
        },
      });

      setSeason({
        ...season,
        tsDivisionId: resp.data?.createSeason?.tsDivisionId,
      });

      setConfirmationType(ConfirmationType.SEASON_CREATED);

      reactiveFeedbackMessage({
        message: 'Your season has been created.',
        isError: false,
        autoclose: true,
      });

      onCreationComplete && onCreationComplete();

    } catch (e) {
      setConfirmationType(ConfirmationType.SEASON_FAIL);

      const errorMessage = 'Creation of season failed. Please try again.';
      reactiveFeedbackMessage({
        message: errorMessage,
        isError: true,
        autoclose: true,
      });
    }

    setShowConfirmModal(true);
  };

  const closeStepper = () => {
    setOpen(false);
  };

  const isHiddenSeasonStepGroup  = (season: SeasonData) => !season.organizeDivisionsByGender && !season.organizeDivisionsByAge;

  const breadcrumb: BreadcrumbSection[] = [
    programBreadcrumb,
    {
      name: 'Season/Division Setup',
      onClick: () => {
        updateCurrentBreadcrumb(SeasonGuideSteps.NAME);
        setCurrentStep(0);
      },
      children: [
        {
          name: SeasonGuideSteps.NAME,
          isValid: season.name !== '',
          onClick: () => {
            updateCurrentBreadcrumb(SeasonGuideSteps.NAME);
            setCurrentStep(0);
          },
        },
        {
          name: SeasonGuideSteps.DIVISION,
          isValid: !shouldDisableDivisionRulesStep(season),
          onClick: () => {
            updateCurrentBreadcrumb(SeasonGuideSteps.DIVISION);
            setCurrentStep(1);
          },
        },
        {
          name: SeasonGuideSteps.AGE_RANGE,
          hidden: isHiddenSeasonStepGroup(season),
          onClick: () => {
            updateCurrentBreadcrumb(SeasonGuideSteps.AGE_RANGE);
            setCurrentStep(2);
          },
        },
        {
          name: SeasonGuideSteps.AGE_GROUPS,
          hidden: isHiddenSeasonStepGroup(season),
          onClick: () => {
            updateCurrentBreadcrumb(SeasonGuideSteps.AGE_GROUPS);
            setCurrentStep(3);
          },
        },
        {
          name: SeasonGuideSteps.GENDERS_BY_AGE,
          hidden: isHiddenSeasonStepGroup(season),
          onClick: () => {
            updateCurrentBreadcrumb(SeasonGuideSteps.GENDERS_BY_AGE);
            setCurrentStep(4);
          },
        },
        {
          name: SeasonGuideSteps.DIVISIONS_REVIEW,
          onClick: () => {
            updateCurrentBreadcrumb(SeasonGuideSteps.DIVISIONS_REVIEW);
            setCurrentStep(5);
          },
        },
      ],
    },
  ];

  return (
    <SeasonGuideContext.Provider value={{ season, setSeason: (x) => setSeason({ ...season, ...x }) }}>
      {!showConfirmModal && (
        <Stepper
          show
          showProgress={false}
          activeStep={currentStep}
          onClose={() => setOpen(false)}
          displayBackOnFirst={false}
          drawer={<Breadcrumb current={currentBreadcrumb} breadcrumb={breadcrumb} data={season} />}
          customHeader={<CustomHeader text={organization?.name} closeStepper={() => setOpen(false)} />}
          customStepStyles={{ paddingLeft: '300px' }}
          steps={[
            {
              title: 'Season',
              heading: 'What would you like to call this season? *',
              disableNext: season.name.trim() === '',
              elements: <NameAndPeriod />,
              stepCallback: () => {
                updateCurrentBreadcrumb(SeasonGuideSteps.DIVISION);
                setCurrentStep(1);
              },
            },

            {
              title: 'Division rules',
              disableNext: shouldDisableDivisionRulesStep(season),
              elements: <DivisionRules></DivisionRules>,
              stepCallback: () => {
                if (season.organizeMultipleDivisions) {
                  updateCurrentBreadcrumb(SeasonGuideSteps.AGE_RANGE);
                  setCurrentStep(2);
                } else {
                  updateCurrentBreadcrumb(SeasonGuideSteps.AGE_GROUPS);
                  setCurrentStep(5);
                }
              },
              stepBackCallback: () => {
                updateCurrentBreadcrumb(SeasonGuideSteps.NAME);
                setCurrentStep(0);
              },
            },
            {
              title: SeasonGuideSteps.AGE_RANGE,
              elements: <AgeRange />,
              disableNext: shouldDisableAgeRangeNext(season),
              stepCallback: () => {
                updateCurrentBreadcrumb(SeasonGuideSteps.AGE_GROUPS);
                setCurrentStep(3);
              },
              stepBackCallback: () => {
                updateCurrentBreadcrumb(SeasonGuideSteps.DIVISION);
                setCurrentStep(1);
              },
            },
            {
              title: SeasonGuideSteps.AGE_GROUPS,
              elements: <SelectAgeGroups />,
              stepCallback: () => {
                if (season.organizeDivisionsByGender) {
                  updateCurrentBreadcrumb(SeasonGuideSteps.GENDERS_BY_AGE);
                  setCurrentStep(4);
                } else {
                  updateCurrentBreadcrumb(SeasonGuideSteps.DIVISIONS_REVIEW);
                  setCurrentStep(5);
                }
              },
              stepBackCallback: () => {
                updateCurrentBreadcrumb(SeasonGuideSteps.AGE_RANGE);
                setCurrentStep(2);
              },
            },
            {
              title: SeasonGuideSteps.GENDERS_BY_AGE,
              heading: 'What divisions do you have within each age group?*',
              elements: <GendersByAge />,
              stepCallback: () => {
                updateCurrentBreadcrumb(SeasonGuideSteps.DIVISIONS_REVIEW);
                setCurrentStep(5);
              },
              stepBackCallback: () => {
                updateCurrentBreadcrumb(SeasonGuideSteps.AGE_GROUPS);
                setCurrentStep(3);
              },
            },
            {
              title: SeasonGuideSteps.DIVISIONS_REVIEW,
              elements: <DivisionsReview />,
              customNextButtonText: 'Create Season',
              customStepStyle: { maxWidth: '800px', marginTop: '100px' },
              stepCallback: () => {
                updateCurrentBreadcrumb(SeasonGuideSteps.DIVISIONS_CONFIRMATION);
                setCurrentStep(6);
                submitCreateSeason();
              },
              stepBackCallback: () => {
                if (!season.organizeMultipleDivisions) {
                  updateCurrentBreadcrumb(SeasonGuideSteps.DIVISION);
                  setCurrentStep(1);
                } else {
                  updateCurrentBreadcrumb(SeasonGuideSteps.GENDERS_BY_AGE);
                  setCurrentStep(4);
                }
              },
            },
            {
              title: SeasonGuideSteps.CREATING_SEASON_DIVISION,
              customNextButtonText: 'Create Season',
              elements: (
                <div style={{ height: '45vh' }} className="u-flex u-flexCol u-flexJustifyCenter u-flexAlignItemsCenter">
                  <h2>Creating Season/Divisions...</h2>
                </div>
              ),
            },
          ]}
        />
      )}
      <ConfirmationModal
        title={program?.name?.trim()}
        show={showConfirmModal}
        onSubmit={closeStepper}
        confirmationType={confirmationType}
        onClose={() => {
          closeStepper();
          setShowConfirmModal(false);
        }}
        season={season}
        redirect={
          organization.registrationType === 'reg1'
            ? `${TEAMSNAP_URL}/divisions/${season.tsDivisionId}/registration`
            : `/organizations/${organization.id}/registration/new`
        }
      />
    </SeasonGuideContext.Provider>
  );
};

export default SeasonGuide;
